import { useState, useMemo, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, PackageOpen, Upload, Search, Loader2 } from 'lucide-react';
import { api } from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';
import { useEmitenteAtivo } from '../context/EmitenteAtivoContext';

interface NFe {
  id: number;
  numero: number;
  serie: number;
  chave: string | null;
  status: string;
  data_emissao: string | null;
  valor_total: number | null;
  destinatario_id: number | null;
}

async function listarAutorizadas(emitenteId: number): Promise<NFe[]> {
  const { data } = await api.get<NFe[]>('/nfe', {
    params: { emitente_id: emitenteId, status: 'autorizada', limit: 200 },
  });
  return data;
}

export default function DevolucaoPage() {
  const navigate = useNavigate();
  const { emitenteId } = useEmitenteAtivo();
  const [aba, setAba] = useState<'historico' | 'xml'>('historico');
  const [busca, setBusca] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [feedback, setFeedback] = useState<{ tipo: 'ok' | 'err'; msg: string } | null>(null);

  const q = useQuery({
    queryKey: ['nfe-autorizadas', emitenteId],
    queryFn: () => listarAutorizadas(emitenteId!),
    enabled: !!emitenteId && aba === 'historico',
  });

  const filtradas = useMemo(() => {
    const all = q.data ?? [];
    if (!busca.trim()) return all;
    const t = busca.toLowerCase();
    return all.filter(
      (n) =>
        String(n.numero).includes(t) ||
        (n.chave || '').toLowerCase().includes(t) ||
        String(n.valor_total ?? '').includes(t),
    );
  }, [q.data, busca]);

  async function gerarDevolucao(nfeId: number) {
    setEnviando(true);
    setFeedback(null);
    try {
      const r = await fetch(`/api/nfe/${nfeId}/devolucao`, { method: 'POST' });
      const data = await r.json();
      if (r.ok && data.id) {
        navigate(`/documentos/nfe/${data.id}?devolucao=true`);
      } else {
        setFeedback({ tipo: 'err', msg: data.error || data.detail || 'Falha ao gerar devolução.' });
      }
    } catch (e) {
      setFeedback({ tipo: 'err', msg: String(e) });
    } finally {
      setEnviando(false);
    }
  }

  async function uploadXml(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !emitenteId) return;
    setEnviando(true);
    setFeedback(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('emitente_id', String(emitenteId));
      const r = await fetch('/api/nfe/devolucao-xml-alheio', { method: 'POST', body: fd });
      const data = await r.json();
      if (r.ok && data.id) {
        setFeedback({ tipo: 'ok', msg: `Rascunho de devolução #${data.id} criado a partir do XML.` });
        setTimeout(() => navigate(`/documentos/nfe/${data.id}?devolucao=true`), 1200);
      } else {
        setFeedback({ tipo: 'err', msg: data.error || data.detail || 'Falha ao processar XML.' });
      }
    } catch (e2) {
      setFeedback({ tipo: 'err', msg: String(e2) });
    } finally {
      setEnviando(false);
      e.target.value = '';
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="h-9 w-9 rounded-lg border border-slate-200 bg-white grid place-items-center hover:bg-slate-100 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <div className="eyebrow">NF-e</div>
          <h1 className="h-display text-xl text-slate-950 mt-0.5 flex items-center gap-2 dark:text-white">
            <PackageOpen className="h-5 w-5 text-amber-700 dark:text-amber-400" /> Devolução
          </h1>
        </div>
      </div>

      {!emitenteId && (
        <div className="card-surface p-6 text-center text-sm text-amber-800 dark:text-amber-300">
          Selecione uma empresa no menu superior para começar.
        </div>
      )}

      {emitenteId && (
        <>
          <div className="card-surface p-2 inline-flex gap-1 rounded-lg">
            <button
              type="button"
              onClick={() => setAba('historico')}
              className={`px-3 py-1.5 text-xs rounded-md font-semibold transition ${
                aba === 'historico' ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300' : 'text-neutral-500 hover:text-slate-950 dark:text-neutral-400 dark:hover:text-white'
              }`}
            >
              <Search className="h-3.5 w-3.5 inline mr-1" /> Buscar no histórico
            </button>
            <button
              type="button"
              onClick={() => setAba('xml')}
              className={`px-3 py-1.5 text-xs rounded-md font-semibold transition ${
                aba === 'xml' ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300' : 'text-neutral-500 hover:text-slate-950 dark:text-neutral-400 dark:hover:text-white'
              }`}
            >
              <Upload className="h-3.5 w-3.5 inline mr-1" /> Importar XML
            </button>
          </div>

          {feedback && (
            <div
              className={`rounded-lg border px-3 py-2 text-sm ${
                feedback.tipo === 'ok'
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-300'
                  : 'border-rose-200 bg-rose-50 text-rose-800 dark:bg-rose-500/10 dark:border-rose-500/30 dark:text-rose-300'
              }`}
            >
              {feedback.msg}
            </div>
          )}

          {aba === 'historico' && (
            <>
              <div className="card-surface p-3">
                <input
                  className="field-input"
                  placeholder="Buscar por número, chave, valor..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                />
              </div>
              {q.isLoading && <div className="py-10 grid place-items-center"><LoadingSpinner /></div>}
              {!q.isLoading && filtradas.length === 0 && (
                <div className="card-surface p-10 text-center text-sm text-neutral-600 dark:text-neutral-400">
                  Nenhuma NF-e autorizada encontrada para esta empresa.
                </div>
              )}
              {!q.isLoading && filtradas.length > 0 && (
                <div className="card-surface overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-wide text-neutral-500 border-b border-slate-200 dark:border-white/10">
                      <tr>
                        <th className="py-2 px-3">Número</th>
                        <th className="py-2 px-3">Série</th>
                        <th className="py-2 px-3">Emissão</th>
                        <th className="py-2 px-3">Valor</th>
                        <th className="py-2 px-3">Chave</th>
                        <th className="py-2 px-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                      {filtradas.map((n) => (
                        <tr key={n.id} className="hover:bg-slate-50 dark:hover:bg-white/[0.02]">
                          <td className="py-2 px-3 mono">{n.numero}</td>
                          <td className="py-2 px-3">{n.serie}</td>
                          <td className="py-2 px-3 text-xs text-neutral-600 dark:text-neutral-400 whitespace-nowrap">
                            {n.data_emissao ? new Date(n.data_emissao).toLocaleDateString('pt-BR') : '-'}
                          </td>
                          <td className="py-2 px-3 mono tab-nums">
                            {n.valor_total != null ? `R$ ${Number(n.valor_total).toFixed(2)}` : '-'}
                          </td>
                          <td className="py-2 px-3 mono text-xs text-neutral-600 dark:text-neutral-400 max-w-[14rem] truncate" title={n.chave || ''}>
                            {n.chave || '-'}
                          </td>
                          <td className="py-2 px-3 text-right">
                            <button
                              type="button"
                              disabled={enviando}
                              onClick={() => gerarDevolucao(n.id)}
                              className="btn-amber text-[10px] !py-0.5 !px-1.5"
                            >
                              {enviando ? <Loader2 className="h-3 w-3 animate-spin" /> : <PackageOpen className="h-3 w-3" />}
                              Devolver
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {aba === 'xml' && (
            <div className="card-surface p-6 space-y-4">
              <p className="text-sm text-neutral-600 dark:text-neutral-300">
                Faça upload do XML de uma NF-e autorizada na SEFAZ, recebida de fornecedor, para gerar
                automaticamente o rascunho da devolução com CFOPs invertidos e referência cruzada.
              </p>
              <label className="block">
                <input
                  type="file"
                  accept=".xml"
                  onChange={uploadXml}
                  disabled={enviando}
                  className="block w-full text-sm text-neutral-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-amber-100 file:text-amber-700 hover:file:bg-amber-200 dark:text-neutral-300 dark:file:bg-amber-500/20 dark:file:text-amber-300 dark:hover:file:bg-amber-500/30"
                />
              </label>
              {enviando && <div className="text-xs text-neutral-600 dark:text-neutral-400">Processando XML...</div>}
              <p className="text-xs text-neutral-500">
                Pessoa fornecedora será criada/encontrada automaticamente pelo CNPJ do XML.
                Itens vêm com CFOP invertido (5.x para 1.x). Você pode ajustar tudo antes de transmitir.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
